import { Button, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, useApiClient } from '@startupquickstart/frontend';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AudioLines } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

interface GenerateVoiceoversActionProps {
  attractionId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function GenerateVoiceoversAction({ attractionId, open, onOpenChange }: GenerateVoiceoversActionProps) {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const [voiceoverType, setVoiceoverType] = useState<'both' | 'description' | 'long_description'>('both');
  const [voice, setVoice] = useState<'alloy' | 'echo' | 'fable' | 'onyx' | 'nova' | 'shimmer'>('alloy');

  const generateVoiceoversMutation = useMutation({
    mutationFn: async (data: { voiceoverType: string; voice: string }) => {
      return apiClient.post(`/attractions/${attractionId}/generate-voiceovers`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['Attraction', 'detail', attractionId] });
      onOpenChange(false);
      toast.success('Voiceovers generated successfully');
    },
    onError: (error: Error) => {
      toast.error(error?.message || 'Failed to generate voiceovers');
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            generateVoiceoversMutation.mutate({ voiceoverType, voice });
          }}
        >
          <DialogHeader>
            <DialogTitle>Generate Voiceovers</DialogTitle>
            <DialogDescription>
              Generate voiceovers for this attraction. This will generate a voiceover for this
              attraction using the description, long description.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <label htmlFor="voiceoverType" className="block text-sm font-medium mb-1">
                Voiceover Type <span className="text-destructive">*</span>
              </label>
              <select
                id="voiceoverType"
                required
                className="w-full px-3 py-2 border rounded-md"
                value={voiceoverType}
                onChange={(e) =>
                  setVoiceoverType(
                    e.target.value as 'both' | 'description' | 'long_description',
                  )
                }
              >
                <option value="both">Both</option>
                <option value="description">Short Description</option>
                <option value="long_description">Long Description</option>
              </select>
            </div>

            <div>
              <label htmlFor="voice" className="block text-sm font-medium mb-1">
                Voice <span className="text-destructive">*</span>
              </label>
              <select
                id="voice"
                required
                className="w-full px-3 py-2 border rounded-md"
                value={voice}
                onChange={(e) =>
                  setVoice(
                    e.target.value as 'alloy' | 'echo' | 'fable' | 'onyx' | 'nova' | 'shimmer',
                  )
                }
              >
                <option value="alloy">Alloy</option>
                <option value="echo">Echo</option>
                <option value="fable">Fable</option>
                <option value="onyx">Onyx</option>
                <option value="nova">Nova</option>
                <option value="shimmer">Shimmer</option>
              </select>
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" loading={generateVoiceoversMutation.isPending}>
              Generate Voiceovers
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// Export the icon for use in the custom action
export const GenerateVoiceoversIcon = AudioLines;
